// ── Booking provider registry (client-side) ───────────────────────────────────
// Used by Admin.js ConciergePanel to know which providers are configured,
// display their status, and trigger bookings via the book-venue edge function.
//
// To activate a provider:
//   1. Implement its TODO stubs in supabase/functions/book-venue/providers/<name>.ts
//   2. Set the required secrets in Supabase dashboard → Edge Functions → Secrets
//   3. Set active: true below so the admin UI offers it as an option
//   4. Add venue_providers rows for each venue that supports the provider

export const BOOKING_PROVIDERS = {
  manual: {
    id:          'manual',
    name:        'Manuel (email)',
    description: 'Génère un email de demande à copier ou envoyer automatiquement via Resend.',
    active:      true,    // Always available — no external API needed
    requiresKey: false,
    color:       '#5B9BD5',
    icon:        '✉️',
    phase:       2,
  },
  thefork: {
    id:          'thefork',
    name:        'TheFork',
    description: 'Réservation automatique via l\'API TheFork / LaFourchette.',
    active:      false,   // Set to true after implementing providers/thefork.ts
    requiresKey: true,
    secretName:  'BOOKING_API_KEY_THEFORK',
    docsUrl:     'https://developers.thefork.com',
    color:       '#00B37A',
    icon:        '🍴',
    phase:       3,
  },
  resy: {
    id:          'resy',
    name:        'Resy',
    description: 'Réservation automatique via l\'API Resy.',
    active:      false,   // Set to true after implementing providers/resy.ts
    requiresKey: true,
    secretName:  'BOOKING_API_KEY_RESY',
    docsUrl:     'https://subzero.resy.com',
    color:       '#E63A3A',
    icon:        '📅',
    phase:       3,
  },
  opentable: {
    id:          'opentable',
    name:        'OpenTable',
    description: 'Réservation automatique via l\'API OpenTable Connect.',
    active:      false,   // Set to true after implementing providers/opentable.ts
    requiresKey: true,
    secretName:  'BOOKING_CLIENT_ID_OPENTABLE',
    docsUrl:     'https://platform.opentable.com/documentation',
    color:       '#DA3743',
    icon:        '🍽️',
    phase:       3,
  },
};

export const ACTIVE_PROVIDERS = Object.values(BOOKING_PROVIDERS).filter(p => p.active);

// Call the book-venue edge function from admin panel
export async function triggerBooking({ supabase, scoutBookingId, action = 'book' }) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const res = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/book-venue`,
    {
      method: 'POST',
      headers: {
        'Authorization':  `Bearer ${token}`,
        'Content-Type':   'application/json',
      },
      body: JSON.stringify({ scout_booking_id: scoutBookingId, action }),
    }
  );

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Create or update a scout_booking record before calling the edge function.
// Throws if a confirmed booking already exists — admin must cancel it first.
export async function upsertScoutBooking({ supabase, scoutId, venueId, date, time, partySize, provider = 'manual', adminUserId }) {
  // Guard: don't overwrite a confirmed booking
  const { data: existing } = await supabase
    .from('scout_bookings')
    .select('id, status')
    .eq('scout_id', scoutId)
    .maybeSingle();

  if (existing?.status === 'confirmed') {
    throw new Error('Une réservation confirmée existe déjà pour ce cercle. Annulez-la avant d\'en créer une nouvelle.');
  }

  const { data, error } = await supabase
    .from('scout_bookings')
    .upsert({
      scout_id:   scoutId,
      venue_id:    venueId,
      status:      'in_progress',
      booked_date: date || null,
      booked_time: time || null,
      party_size:  partySize || null,
      provider,
      booked_by:   adminUserId,
    }, { onConflict: 'scout_id' })
    .select('id')
    .single();

  if (error) throw new Error(error.message);
  return data;
}
